/**
 * steps/WelcomeStep.tsx
 * Welcome step for the onboarding wizard
 *
 * Introduces users to the trading journal with:
 * - 7-card progressive educational flow
 * - Setup overview of what will be configured
 * - Scroll-based navigation with reading progress
 */

// 1. External dependencies
import React, { useState, useEffect, useRef, useCallback } from 'react';

// 2. Workspace packages
import { Card, CardHeader, CardBody, CardFooter, Button, BrandMark } from '@enfomentis/ui';
import { Brand } from '@enfomentis/shared/config';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';

/**
 * Custom hook for intersection observer
 */
const useIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options?: Partial<{
    threshold: number;
    root: Element | null;
    rootMargin: string;
  }>
) => {
  const [elements, setElements] = useState<Element[]>([]);

  useEffect(() => {
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(callback, {
      threshold: 0.3,
      ...options
    });

    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [elements, callback, options]);

  const addElement = useCallback((element: Element | null) => {
    if (element && !elements.includes(element)) {
      setElements(prev => [...prev, element]);
    }
  }, [elements]);

  return addElement;
};

/**
 * Welcome step component props
 */
interface WelcomeStepProps {
  goToNextStep: () => void;
}

/**
 * Welcome step component
 */
export const WelcomeStep: React.FC<WelcomeStepProps> = ({ goToNextStep }) => {
  const { markCompleted } = useOnboardingData();

  // State for scroll-based engagement
  const [revealedSections, setRevealedSections] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Intersection observer callback
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const sectionId = entry.target.getAttribute('data-section-id');
        if (sectionId) {
          setRevealedSections(prev => new Set([...prev, sectionId]));
        }
      }
    });
  }, []);

  // Set up intersection observer
  const addToObserver = useIntersectionObserver(handleIntersection);

  // Show welcome section immediately on mount
  useEffect(() => {
    setRevealedSections(prev => new Set([...prev, 'welcome']));
  }, []);

  // Handler for start button - marks complete and goes to next step
  const handleStart = useCallback(() => {
    markCompleted('welcome');
    goToNextStep();
  }, [markCompleted, goToNextStep]);

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-dvh w-full flex flex-col border-0"
    >
      <CardBody className="flex-1 p-0">
        <div
          ref={scrollContainerRef}
          className="h-full overflow-y-auto px-6 scroll-smooth [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-default-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-0 scrollbar-thin scrollbar-thumb-default-300 scrollbar-track-transparent"
          style={{
            scrollSnapType: 'y mandatory',
            scrollBehavior: 'smooth'
          }}
        >

          {/* Card 1: Welcome */}
          <div
            data-section-id="welcome"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 ${
              revealedSections.has('welcome')
                ? 'opacity-100 translate-y-0'
                : 'opacity-100 translate-y-0'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card radius="none" shadow="none" className="w-full max-w-2xl">
              <CardBody className="text-center py-8">
                <div className="inline-flex items-center justify-center mb-6 mx-auto rounded-lg p-4 bg-primary/5 border border-primary/10">
                  <BrandMark size={180} />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-text-primary">
                  Welcome to {Brand.displayName}
                </h1>
              </CardBody>
            </Card>
          </div>

          {/* Card 2: Thank You */}
          <div
            data-section-id="thank-you"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-100 ${
              revealedSections.has('thank-you')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Thank You
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Thank you for choosing {Brand.displayName}. You're taking an important step toward trading with discipline, clarity, and continuous improvement.
                </p>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1 pt-5">
                <div><span className="text-primary">Professional traders</span> don't trade on gut alone - they follow systems. You're about to build yours.</div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 3: One-Time Setup */}
          <div
            data-section-id="setup"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-200 ${
              revealedSections.has('setup')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                One-Time Setup, Lifetime Benefits
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  This onboarding takes 10-15 minutes to complete. Once finished, you'll have a <span className="text-primary">personalized trading journal</span> that grows with you.
                </p>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1 pt-5">
                Every trade you take, every lesson you learn, captured and analyzed in one place.
              </CardFooter>
            </Card>
          </div>

          {/* Card 4: Your Trading Account */}
          <div
            data-section-id="account"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-300 ${
              revealedSections.has('account')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Your Trading Account
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  First, we'll set up your account details - what you trade, which broker you use, and your fee structure.
                </p>
                
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1 pt-5">
                <div>This ensures <span className="text-primary">accurate P&L tracking and performance analysis.</span></div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 5: Your Trading Strategy */}
          <div
            data-section-id="strategy"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-400 ${
              revealedSections.has('strategy')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Your Trading Strategy
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Define your trading approach with strategies, playbooks, criteria, and entry models.
                </p>
                
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1 pt-5">
                <div>This creates a <span className="text-primary">systematic framework</span> for every trade decision.</div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 6: Risk Management */}
          <div
            data-section-id="risk"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-500 ${
              revealedSections.has('risk')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Risk Management Policies
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Set your position sizing rules, risk limits, and drawdown policies.
                </p>
               
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1 pt-5">
                <div>These guardrails keep you <span className="text-primary">disciplined</span> even when emotions run high.</div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 7: Your Workflow */}
          <div
            data-section-id="workflow"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-600 ${
              revealedSections.has('workflow')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Your Preferred Workflow
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Configure your daily routine - pre-trade planning, execution tracking, post-trade review.
                </p>
                
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1 pt-5">
                <div>Build habits that compound into <span className="text-primary">consistent results.</span></div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 8: Ready to Get Started */}
          <div
            data-section-id="ready"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center transition-all duration-700 delay-700 ${
              revealedSections.has('ready')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Ready to Build Your Trading Journal?
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  In the next 10-15 minutes, you'll configure your account, strategy, risk policies, and workflow.
                </p>
              </CardBody>
              <CardFooter className="border-t-1 pt-6 pb-6">
                <Button
                  color="primary"
                  size="lg"
                  className="w-full px-8"
                  onPress={handleStart}
                >
                  Start Setup
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};